"use client";

import { useEffect, useRef, useState, useCallback, type FC } from "react";
import mermaid from "mermaid";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, RotateCcw, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Initialize mermaid with default config
mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: "inherit",
});

/**
 * Sanitize Mermaid chart syntax to fix common issues from LLM-generated diagrams.
 *
 * LLMs frequently generate Mermaid with unquoted labels containing special chars
 * (quotes, parens, colons, etc.) that break the parser.
 *
 * Strategy: find node labels by matching balanced bracket pairs, then quote them.
 */
function sanitizeMermaidCode(code: string): string {
  let sanitized = code;

  // Fix self-closing <br/> tags
  sanitized = sanitized.replace(/<br\s*\/>/gi, "<br>");

  // Process line by line
  sanitized = sanitized.split('\n').map(line => {
    const trimmed = line.trim();

    // Skip non-node lines
    if (!trimmed ||
        trimmed.startsWith('%%') ||
        /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|gitGraph|journey|mindmap|timeline)\b/i.test(trimmed) ||
        /^(subgraph|end|direction|style|classDef|click|linkStyle|class)\b/i.test(trimmed) ||
        /^(participant|actor|Note|loop|alt|else|opt|par|rect|activate|deactivate|title|section)\b/i.test(trimmed)) {
      return line;
    }

    // Fix node labels by finding balanced bracket pairs and quoting their contents.
    // Handles: A[label], A(label), A{label}, A([label]), A((label))
    // Uses a character-by-character scan to find balanced delimiters.
    line = fixNodeLabels(line);

    // Fix arrow labels with special chars: -->|label|
    line = line.replace(/-->\|([^|]+)\|/g, (_match, label) => {
      if (/[()":;!?=<>{}@#$%^&*~`]/.test(label)) {
        return `-->|"${quoteLabel(label)}"|`;
      }
      return _match;
    });

    return line;
  }).join('\n');

  return sanitized;
}

/** Replace quotes and special chars to make a label safe inside Mermaid quotes */
function quoteLabel(label: string): string {
  return label.replace(/"/g, "'");
}

/**
 * Scan a line and fix node labels by finding bracket pairs and quoting contents.
 * Handles nested brackets like A([label with (parens)]).
 */
function fixNodeLabels(line: string): string {
  const result: string[] = [];
  let i = 0;

  while (i < line.length) {
    // Look for a node ID followed by an opening bracket
    // Node IDs are word chars (A, B1, nodeId, etc.)
    if (/\w/.test(line[i])) {
      // Collect the node ID
      let idStart = i;
      while (i < line.length && /\w/.test(line[i])) i++;
      const nodeId = line.slice(idStart, i);

      // Check for opening bracket right after ID
      if (i < line.length && '([{'.includes(line[i])) {
        const openChar = line[i];
        const closeChar = openChar === '[' ? ']' : openChar === '(' ? ')' : '}';

        // Find the matching close bracket, handling nesting
        let depth = 1;
        let j = i + 1;
        while (j < line.length && depth > 0) {
          if (line[j] === openChar) depth++;
          else if (line[j] === closeChar) depth--;
          if (depth > 0) j++;
        }

        if (depth === 0) {
          // We found a balanced pair: line[i] to line[j] inclusive
          const innerContent = line.slice(i + 1, j);

          // Check if this label needs quoting
          // Already quoted? Skip.
          if (innerContent.startsWith('"') && innerContent.endsWith('"')) {
            result.push(nodeId + line.slice(i, j + 1));
          }
          // Contains inner brackets (like ([...]) or ((...))), handle recursively
          else if (openChar === '(' && (innerContent.startsWith('[') || innerContent.startsWith('('))) {
            // Stadium ([...]) or double-circle ((...)) — extract inner label
            const innerOpen = innerContent[0];
            const innerClose = innerOpen === '[' ? ']' : ')';
            const innerEnd = innerContent.lastIndexOf(innerClose);
            if (innerEnd > 0) {
              const label = innerContent.slice(1, innerEnd);
              if (/[()":;,!?=<>@#$%^&*~`]/.test(label) || label.includes('"')) {
                result.push(`${nodeId}(${innerOpen}"${quoteLabel(label)}"${innerClose})`);
              } else {
                result.push(nodeId + line.slice(i, j + 1));
              }
            } else {
              result.push(nodeId + line.slice(i, j + 1));
            }
          }
          // Regular bracket label — quote if it has special chars
          else if (/[":;!?=<>@#$%^&*~`]/.test(innerContent) ||
                   (openChar === '[' && /[()]/.test(innerContent)) ||
                   // Unicode chars like ≈
                   /[^\x00-\x7F]/.test(innerContent)) {
            result.push(`${nodeId}${openChar}"${quoteLabel(innerContent)}"${closeChar}`);
          } else {
            result.push(nodeId + line.slice(i, j + 1));
          }
          i = j + 1;
        } else {
          // Unbalanced — output as-is
          result.push(nodeId);
          // Don't advance i, let next iteration handle the bracket
        }
      } else {
        result.push(nodeId);
      }
    } else {
      result.push(line[i]);
      i++;
    }
  }

  return result.join('');
}

interface ZoomControlsProps {
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  showFullscreenButton?: boolean;
}

/**
 * Zoom controls component for mermaid diagrams
 */
const ZoomControls: FC<ZoomControlsProps> = ({
  isFullscreen = false,
  onToggleFullscreen,
  showFullscreenButton = true
}) => {
  const { zoomIn, zoomOut, resetTransform, centerView } = useControls();

  return (
    <div className="absolute top-2 right-2 flex gap-1 rounded-lg bg-background/80 backdrop-blur-sm border border-border p-1 z-10">
      <button
        onClick={() => zoomIn()}
        className="p-1.5 rounded hover:bg-muted transition-colors"
        title="Zoom in"
      >
        <ZoomIn className="h-4 w-4" />
      </button>
      <button
        onClick={() => zoomOut()}
        className="p-1.5 rounded hover:bg-muted transition-colors"
        title="Zoom out"
      >
        <ZoomOut className="h-4 w-4" />
      </button>
      <button
        onClick={() => centerView()}
        className="p-1.5 rounded hover:bg-muted transition-colors"
        title="Center view"
      >
        <Maximize2 className="h-4 w-4" />
      </button>
      <button
        onClick={() => resetTransform()}
        className="p-1.5 rounded hover:bg-muted transition-colors"
        title="Reset zoom"
      >
        <RotateCcw className="h-4 w-4" />
      </button>
      {showFullscreenButton && onToggleFullscreen && (
        <>
          <div className="w-px bg-border mx-0.5" />
          <button
            onClick={onToggleFullscreen}
            className="p-1.5 rounded hover:bg-muted transition-colors"
            title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </button>
        </>
      )}
    </div>
  );
};

interface DiagramViewerProps {
  svg: string;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

/**
 * Diagram viewer with zoom/pan controls
 */
const DiagramViewer: FC<DiagramViewerProps> = ({ svg, isFullscreen = false, onToggleFullscreen }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Extreme zoom for fullscreen mode, normal zoom for inline
  const maxScale = isFullscreen ? 20 : 10;
  const minScale = isFullscreen ? 0.1 : 0.5;

  return (
    <TransformWrapper
      initialScale={1}
      minScale={minScale}
      maxScale={maxScale}
      centerOnInit={true}
      wheel={{ step: 0.1 }}
      panning={{ velocityDisabled: true }}
      doubleClick={{ mode: "zoomIn", step: 0.5 }}
    >
      <ZoomControls
        isFullscreen={isFullscreen}
        onToggleFullscreen={onToggleFullscreen}
        showFullscreenButton={!!onToggleFullscreen}
      />
      <TransformComponent
        wrapperStyle={{
          width: "100%",
          height: isFullscreen ? "100%" : "400px",
        }}
        contentStyle={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <div
          ref={containerRef}
          className="p-4"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </TransformComponent>
    </TransformWrapper>
  );
};

/**
 * Fullscreen overlay for mermaid diagrams
 */
const FullscreenOverlay: FC<{ svg: string; onClose: () => void }> = ({ svg, onClose }) => {
  // Handle escape key to close fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    // Prevent body scroll when fullscreen is open
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
      {/* Close button in top-left */}
      <button
        onClick={onClose}
        className="absolute top-4 left-4 z-20 p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
        title="Close fullscreen (Esc)"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Diagram viewer */}
      <div className="w-full h-full">
        <DiagramViewer
          svg={svg}
          isFullscreen={true}
          onToggleFullscreen={onClose}
        />
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-4 text-sm text-muted-foreground bg-background/80 px-3 py-2 rounded-lg">
        <div>Scroll to zoom (up to 20x) • Drag to pan • Double-click to zoom in</div>
        <div className="text-xs mt-1 opacity-70">Press Esc or click X to exit</div>
      </div>
    </div>
  );
};

/**
 * MermaidDiagram component for rendering Mermaid diagrams in markdown
 * Supports zoom, pan, pinch gestures, and fullscreen view
 */
export const MermaidDiagram: FC<{ code: string }> = ({ code }) => {
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  useEffect(() => {
    if (!code) return;

    const renderDiagram = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Sanitize the code to fix common LLM-generated syntax issues
        const sanitizedCode = sanitizeMermaidCode(code);

        // Generate unique ID for the diagram
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

        // Render the diagram
        const { svg: renderedSvg } = await mermaid.render(id, sanitizedCode);
        setSvg(renderedSvg);
      } catch (err) {
        console.error("Mermaid rendering error:", err);
        setError(err instanceof Error ? err.message : "Failed to render diagram");
      } finally {
        setIsLoading(false);
      }
    };

    renderDiagram();
  }, [code]);

  if (isLoading) {
    return (
      <div className="my-4 flex items-center justify-center rounded-lg border border-border bg-muted/50 p-8">
        <div className="flex items-center gap-2 text-muted-foreground">
          <svg
            className="h-5 w-5 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Rendering diagram...</span>
        </div>
      </div>
    );
  }

  if (error) {
    // Graceful fallback: show the diagram source as a styled code block
    return (
      <div className="my-4 rounded-lg border border-border bg-muted/30 p-4">
        <div className="mb-2 text-xs text-muted-foreground">Diagram</div>
        <pre className="overflow-x-auto rounded bg-black p-4 text-sm text-green-400 font-mono whitespace-pre-wrap">
          {code}
        </pre>
      </div>
    );
  }

  return (
    <>
      {/* Inline view */}
      <div
        className={cn(
          "my-4 relative rounded-lg border border-border bg-white overflow-hidden",
          "dark:bg-gray-900"
        )}
      >
        <DiagramViewer
          svg={svg}
          isFullscreen={false}
          onToggleFullscreen={toggleFullscreen}
        />
        <div className="absolute bottom-2 left-2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
          Scroll to zoom • Drag to pan • Click expand for fullscreen
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <FullscreenOverlay svg={svg} onClose={toggleFullscreen} />
      )}
    </>
  );
};
